import { z } from 'zod';

// ─── Term model shared by `loans` (payable) and `shareholder_loans` (receivable) ──
//
// A note is described by its TERMS, not by a payment count: how often it pays,
// how the rate is stated, how long the payment is amortised over, how many
// payments are actually scheduled (fewer leaves a balloon), and when interest
// starts versus when the first payment falls. See docs/claude/loans.md.

export const LOAN_STATUSES = ['active', 'paid_off'] as const;
export type LoanStatus = (typeof LOAN_STATUSES)[number];

export const PAYMENT_FREQUENCIES = ['weekly', 'biweekly', 'monthly', 'quarterly'] as const;
export type PaymentFrequency = (typeof PAYMENT_FREQUENCIES)[number];

export const PERIODS_PER_YEAR: Record<PaymentFrequency, number> = {
  weekly: 52,
  biweekly: 26,
  monthly: 12,
  quarterly: 4,
};

export const PAYMENT_FREQUENCY_LABELS: Record<PaymentFrequency, string> = {
  weekly: 'Weekly',
  biweekly: 'Every two weeks',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

/**
 * nominal          periodic rate = annual ÷ periods per year (bank-style APR; the
 *                  original behaviour of this register)
 * annual_effective periodic rate = (1 + annual)^(1 / periods per year) − 1 — the
 *                  "annual compounding" basis the IRS publishes AFRs on
 */
export const RATE_BASES = ['nominal', 'annual_effective'] as const;
export type RateBasis = (typeof RATE_BASES)[number];

export const RATE_BASIS_LABELS: Record<RateBasis, string> = {
  nominal: 'Nominal APR (rate ÷ periods)',
  annual_effective: 'Annual compounding (AFR basis)',
};

export const BUSINESS_DAY_RULES = ['none', 'next_business_day'] as const;
export type BusinessDayRule = (typeof BUSINESS_DAY_RULES)[number];

export const LOAN_PAYMENT_KINDS = ['scheduled', 'prepayment', 'balloon', 'payoff', 'late_charge'] as const;
export type LoanPaymentKind = (typeof LOAN_PAYMENT_KINDS)[number];

export const LOAN_PAYMENT_KIND_LABELS: Record<LoanPaymentKind, string> = {
  scheduled: 'Scheduled',
  prepayment: 'Prepayment',
  balloon: 'Balloon',
  payoff: 'Payoff',
  late_charge: 'Late charge',
};

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const nonNegMoney = z.string().regex(/^\d+(\.\d{1,4})?$/, 'must be a non-negative money value');
const positiveMoney = nonNegMoney.refine((v) => Number(v) > 0, 'must be greater than zero');
const nonNegPct = z.string().regex(/^\d+(\.\d{1,6})?$/, 'must be a non-negative percentage');

/** Everything the amortization engine needs. Optional fields fall back as documented. */
export interface LoanTerms {
  principal: string;
  annualRatePct: string;
  rateBasis: RateBasis;
  paymentFrequency: PaymentFrequency;
  /** Sizes the level payment (e.g. 1560 weekly periods = 30 years). */
  amortizationPeriods: number;
  /** How many payments are actually scheduled. Defaults to amortizationPeriods (no balloon). */
  numberOfPayments?: number;
  /** Override; otherwise the level payment over amortizationPeriods. */
  paymentAmount?: string | null;
  /** Interest starts here. */
  originationDate: string;
  /** Every later due date steps from here by the frequency. */
  firstPaymentDate: string;
  businessDayRule: BusinessDayRule;
}

export const loanTermsFields = {
  principal: positiveMoney,
  annualRatePct: nonNegPct.default('0'),
  rateBasis: z.enum(RATE_BASES).default('nominal'),
  paymentFrequency: z.enum(PAYMENT_FREQUENCIES).default('monthly'),
  amortizationPeriods: z.number().int().min(1).max(5200),
  numberOfPayments: z.number().int().min(1).max(5200).optional(),
  paymentAmount: positiveMoney.nullable().optional(),
  originationDate: isoDate,
  firstPaymentDate: isoDate,
  businessDayRule: z.enum(BUSINESS_DAY_RULES).default('next_business_day'),
  lateChargePct: nonNegPct.nullable().optional(),
  lateChargeGraceDays: z.number().int().min(0).max(90).nullable().optional(),
  defaultRateSpreadPct: nonNegPct.nullable().optional(),
};

export function refineLoanTerms(
  v: { amortizationPeriods: number; numberOfPayments?: number | undefined; originationDate: string; firstPaymentDate: string },
  ctx: z.RefinementCtx,
): void {
  if (v.numberOfPayments !== undefined && v.numberOfPayments > v.amortizationPeriods) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['numberOfPayments'],
      message: 'Number of payments cannot exceed the amortization length',
    });
  }
  if (v.firstPaymentDate <= v.originationDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['firstPaymentDate'],
      message: 'First payment must fall after the origination date',
    });
  }
}

/**
 * How the principal arrives on the books. Omit it when the balance already sits
 * in the liability (or receivable) account. Otherwise one origination entry is
 * posted: each `consolidate` row is an existing balance being rolled into this
 * note, each `advances` row is new money moving through a bank account, and the
 * two must sum to the principal exactly.
 */
export const loanFundingSchema = z.object({
  consolidate: z.array(z.object({ accountId: z.string().uuid(), amount: positiveMoney })).max(20).default([]),
  advances: z.array(z.object({ accountId: z.string().uuid(), amount: positiveMoney })).max(20).default([]),
});
export type LoanFundingInput = z.input<typeof loanFundingSchema>;

export const createLoanInputSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    lenderContactId: z.string().uuid().nullable().optional(),
    liabilityAccountId: z.string().uuid(),
    interestAccountId: z.string().uuid(),
    /** Bank account scheduled payments are drawn from. Required when autoPostPayments. */
    paymentAccountId: z.string().uuid().nullable().optional(),
    autoPostPayments: z.boolean().default(false),
    funding: loanFundingSchema.optional(),
    ...loanTermsFields,
  })
  .superRefine((v, ctx) => {
    refineLoanTerms(v, ctx);
    if (v.autoPostPayments && !v.paymentAccountId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['paymentAccountId'],
        message: 'Automatic posting needs a bank account to pay from',
      });
    }
  });
export type CreateLoanInput = z.input<typeof createLoanInputSchema>;

export const recordLoanPaymentInputSchema = z.object({
  paymentDate: isoDate,
  kind: z.enum(LOAN_PAYMENT_KINDS).default('scheduled'),
  /**
   * scheduled: defaults to the installment amount; anything above it is extra principal.
   * prepayment / late_charge: required.
   * balloon / payoff: defaults to the amount owing (balance + accrued interest).
   */
  amount: positiveMoney.optional(),
  /** Bank/cash account the payment is drawn from. */
  paymentAccountId: z.string().uuid(),
  memo: z.string().max(200).nullable().optional(),
});
export type RecordLoanPaymentInput = z.input<typeof recordLoanPaymentInputSchema>;

export interface AmortRow {
  periodNo: number;
  /** Business-day-shifted when the terms say so. */
  dueDate: string;
  payment: string;
  interest: string;
  principal: string;
  balance: string;
  kind: 'scheduled' | 'balloon';
}

export interface Loan {
  id: string;
  companyId: string;
  name: string;
  lenderContactId: string | null;
  liabilityAccountId: string;
  interestAccountId: string;
  paymentAccountId: string | null;
  autoPostPayments: boolean;
  originationJournalEntryId: string | null;
  principal: string;
  annualRatePct: string;
  rateBasis: RateBasis;
  paymentFrequency: PaymentFrequency;
  amortizationPeriods: number;
  numberOfPayments: number;
  paymentAmount: string;
  originationDate: string;
  firstPaymentDate: string;
  maturityDate: string;
  businessDayRule: BusinessDayRule;
  lateChargePct: string | null;
  lateChargeGraceDays: number | null;
  defaultRateSpreadPct: string | null;
  currentBalance: string;
  periodsPaid: number;
  status: LoanStatus;
  /** Derived from the live projection. */
  nextDueDate: string | null;
  nextDueAmount: string | null;
  /** Balance left after the last scheduled payment, from the live projection. */
  projectedBalloon: string;
  /** True when the next scheduled installment is past its due date plus grace. */
  isOverdue: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LoanPaymentRecord {
  id: string;
  paymentDate: string;
  kind: LoanPaymentKind;
  scheduledPeriodNo: number | null;
  dueDate: string | null;
  accruedFrom: string | null;
  accruedTo: string | null;
  amount: string;
  principalPortion: string;
  interestPortion: string;
  lateCharge: string;
  paymentAccountId: string;
  balanceAfter: string;
  journalEntryId: string | null;
  createdAt: string;
}

export interface LoanWithDetails extends Loan {
  /** Remaining schedule projected from the live balance (balloon row last when any). */
  schedule: AmortRow[];
  payments: LoanPaymentRecord[];
}

export interface RecordLoanPaymentResult {
  loan: Loan;
  kind: LoanPaymentKind;
  interest: string;
  principal: string;
  lateCharge: string;
  payment: string;
  journalEntryId: string;
}

/** One loan's outcome inside the scheduled-posting job. */
export interface PostDueLoanPaymentsResult {
  loanId: string;
  companyId: string;
  name: string;
  posted: number;
  error: string | null;
}
